import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface RecordTermsAcceptanceParams {
  clientId: string;
  signatureData: string;
}

export const useTermsSignature = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const recordAcceptance = useMutation({
    mutationFn: async ({ clientId, signatureData }: RecordTermsAcceptanceParams) => {
      console.log('🚀 Starting terms acceptance recording', {
        clientId,
        signatureDataLength: signatureData.length,
        userAgent: navigator.userAgent
      });

      // Get client IP and user agent for audit trail
      const userAgent = navigator.userAgent;
      
      // Note: In a production app, you might want to get the actual client IP
      // For now, we'll pass null and let the database function handle it
      const { data, error } = await supabase
        .rpc('record_client_terms_acceptance', {
          p_client_id: clientId,
          p_signature_data: signatureData,
          p_ip_address: null,
          p_user_agent: userAgent
        });

      console.log('📡 Supabase response:', { data, error });

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }
      
      console.log('✅ Terms acceptance recorded with ID:', data);
      return data;
    },
    onSuccess: (_, variables) => {
      console.log('🔄 Invalidating terms status cache for client:', variables.clientId);
      
      // Invalidate and refetch client terms status
      queryClient.invalidateQueries({ 
        queryKey: ['client-terms-status', variables.clientId] 
      });
      
      // Also refetch immediately to ensure UI updates
      queryClient.refetchQueries({
        queryKey: ['client-terms-status', variables.clientId]
      });
      
      toast({
        title: t('common.success'),
        description: t('clients.termsAcceptance.termsAcceptedSuccess'),
      });
    },
    onError: (error) => {
      console.error('Terms acceptance error:', error);
      toast({
        title: t('common.error'),
        description: t('clients.termsAcceptance.termsAcceptanceError'),
        variant: 'destructive',
      });
    },
  });

  return {
    recordAcceptance: recordAcceptance.mutateAsync,
    isRecording: recordAcceptance.isPending,
  };
};